import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Flower2, Sparkles, Droplet, Wind, Moon, Sun } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CrisisBanner from "../components/CrisisBanner";
import CallButton from "../components/CallButton";
import { useSite } from "../context/SiteContext";

const SPA_HERO = "https://images.unsplash.com/photo-1540555700478-4be289fbecef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxzcGElMjBzdG9uZXMlMjBjYW5kbGV8ZW58MHx8fHwxNzc5MDY5NjgxfDA&ixlib=rb-4.1.0&q=85";
const SPA_RITUAL = "https://images.unsplash.com/photo-1600334129128-685c5582fd35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxzcGElMjB3YXRlciUyMHRoZXJhcHl8ZW58MHx8fHwxNzc5MDY5NjgxfDA&ixlib=rb-4.1.0&q=85";

// Curated gallery — chosen for the way they slow the nervous system down
const GALLERY = [
  { src: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=900&q=80&auto=format&fit=crop", caption: "Hot stone work", span: "md:col-span-2 md:row-span-2" },
  { src: "https://images.unsplash.com/photo-1545203372-781e2f5c7f31?w=600&q=80&auto=format&fit=crop", caption: "Sandalwood + bergamot" },
  { src: "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=600&q=80&auto=format&fit=crop", caption: "Therapeutic touch" },
  { src: "https://images.unsplash.com/photo-1556228852-80b6e5eeff06?w=600&q=80&auto=format&fit=crop", caption: "Aromatherapy ritual" },
  { src: "https://images.unsplash.com/photo-1591343395082-e120087004b4?w=900&q=80&auto=format&fit=crop", caption: "Stillness practice", span: "md:col-span-2" },
  { src: "https://images.unsplash.com/photo-1610304229048-f51d3a48b67d?w=600&q=80&auto=format&fit=crop", caption: "Hand-blended oils" },
  { src: "https://images.unsplash.com/photo-1611073761364-04bcca730bf3?w=600&q=80&auto=format&fit=crop", caption: "The treatment room" },
  { src: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=900&q=80&auto=format&fit=crop", caption: "Breath & quiet", span: "md:col-span-2" },
];

const services = [
  {
    icon: Flower2,
    name: "Therapeutic Massage",
    duration: "60 / 90 min",
    blurb: "Deep tissue, Swedish, or trigger-point work — tailored to where your body is holding stress.",
    tone: "dark",
  },
  {
    icon: Droplet,
    name: "Aromatherapy Ritual",
    duration: "75 min",
    blurb: "Hand-blended essential oils, warm towels, and slow strokes designed to settle the nervous system.",
    tone: "secondary",
  },
  {
    icon: Wind,
    name: "Breath & Body",
    duration: "45 min",
    blurb: "A guided pranayama session followed by gentle body unwinding. For the mind that won't slow down.",
    tone: "surface",
  },
  {
    icon: Moon,
    name: "Restorative Reset",
    duration: "90 min",
    blurb: "Warm stone work + cranial release. Designed for burnout, insomnia, and the deeply depleted.",
    tone: "accent",
  },
  {
    icon: Sparkles,
    name: "Lymphatic Drainage",
    duration: "60 min",
    blurb: "Featherlight strokes that clear, brighten, and de-puff. Excellent post-flight or post-illness.",
    tone: "surface",
  },
  {
    icon: Sun,
    name: "Therapy + Massage Pairing",
    duration: "2 hours",
    blurb: "A 50-min talk session followed by 60 min of bodywork. Mind and body, in the same afternoon.",
    tone: "dark",
  },
];

const tones = {
  dark: "bg-[#3A5A40] text-[#FDFBF7]",
  accent: "bg-[#9F5C45] text-[#FDFBF7]",
  surface: "bg-[#F4F1EA] text-[#1C1917]",
  secondary: "bg-[#EAE5D9] text-[#1C1917]",
};

export default function Spa() {
  const { site } = useSite();

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased overflow-x-hidden" data-testid="spa-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={SPA_HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/40 via-[#FDFBF7]/20 to-[#FDFBF7]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-56 lg:pt-64 w-full">
          <Link to="/" data-testid="spa-back" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors mb-10">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to clinic
          </Link>
          <p className="overline text-[#9F5C45] mb-8" data-testid="spa-hero-overline">The Spa at Let Go Wellness</p>
          <h1
            className="font-serif font-light text-[#1C1917] leading-[0.95] tracking-tight text-balance text-5xl sm:text-6xl md:text-7xl lg:text-[100px] max-w-5xl"
            data-testid="spa-hero-title"
          >
            Where the body is <em className="italic font-light text-[#3A5A40]">heard</em>,
            <br />
            and softens accordingly.
          </h1>
          <div className="mt-12 grid md:grid-cols-2 gap-10 max-w-3xl">
            <p className="text-[#57534E] text-base md:text-lg leading-relaxed">
              The mind and the body are not separate. Our spa offers somatic, body-led
              care — massage therapy, aromatherapy, and breathwork — woven into the
              same trauma-informed philosophy as our talk-therapy practice.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 md:justify-end md:items-end">
              <Link
                to="/#book"
                data-testid="spa-cta-primary"
                className="inline-flex items-center gap-2 px-8 py-4 btn-primary text-sm tracking-wide"
              >
                Book a spa session <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <a
                href={`tel:${(site?.phone || "").replace(/\s+/g, "")}`}
                data-testid="spa-cta-call"
                className="inline-flex items-center gap-2 px-8 py-4 btn-ghost text-sm tracking-wide"
              >
                Call {site?.phone || ""}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="relative py-24 md:py-32 bg-[#F4F1EA]" data-testid="spa-philosophy">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <p className="overline text-[#9F5C45] mb-6">01 — Our approach</p>
            <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
              The body keeps the <em className="italic text-[#3A5A40]">score</em> — and the cure.
            </h2>
            <p className="text-[#57534E] text-base md:text-lg leading-relaxed">
              Talk therapy alone cannot reach the places anxiety, trauma and chronic
              stress live in the body. Our spa is an intentional pairing: licensed
              massage therapists, trained in nervous-system regulation, working in
              the same quiet rooms as our clinicians.
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <img src={SPA_RITUAL} alt="" className="w-full h-[480px] md:h-[640px] object-cover" />
          </div>
        </div>
      </section>

      {/* Gallery — relaxing imagery */}
      <section className="relative py-24 md:py-32" data-testid="spa-gallery">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
            <div className="md:col-span-5">
              <p className="overline text-[#9F5C45] mb-6">A glimpse</p>
              <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Step inside, <em className="italic text-[#3A5A40]">slowly.</em>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="text-[#57534E] text-base md:text-lg leading-relaxed">
                A small set of the rooms, rituals and quiet moments that wait for you here.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[220px] gap-3">
            {GALLERY.map((g, i) => (
              <figure
                key={i}
                data-testid={`spa-gallery-${i}`}
                className={`relative overflow-hidden group ${g.span || ""}`}
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5 text-[#FDFBF7] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="overline text-[#EAE5D9]">{g.caption}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="relative py-24 md:py-32" data-testid="spa-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
            <div className="md:col-span-6">
              <p className="overline text-[#9F5C45] mb-6">02 — Treatments</p>
              <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Six rituals, <em className="italic text-[#3A5A40]">one intention.</em>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="text-[#57534E] text-base md:text-lg leading-relaxed">
                Each session begins with three minutes of stillness — a slow check-in
                with your therapist about what your body needs today.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((s, i) => {
              const Icon = s.icon;
              const isDark = s.tone === "dark" || s.tone === "accent";
              return (
                <article
                  key={s.name}
                  data-testid={`spa-service-${i}`}
                  className={`group p-8 md:p-10 min-h-[300px] flex flex-col justify-between transition-transform duration-500 hover:-translate-y-1 ${tones[s.tone]}`}
                >
                  <div className="flex items-start justify-between">
                    <Icon className={`w-7 h-7 ${isDark ? "text-[#EAE5D9]" : "text-[#3A5A40]"}`} strokeWidth={1.5} />
                    <span className={`overline ${isDark ? "text-[#EAE5D9]/80" : "text-[#9F5C45]"}`}>{s.duration}</span>
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-3">{s.name}</h3>
                    <p className={`text-sm md:text-base leading-relaxed ${isDark ? "text-[#EAE5D9]" : "text-[#57534E]"}`}>
                      {s.blurb}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="relative py-24 md:py-32 bg-[#F4F1EA]" data-testid="spa-expect">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <p className="overline text-[#9F5C45] mb-6">03 — Your visit</p>
            <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              What to <em className="italic text-[#3A5A40]">expect.</em>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-10">
            {[
              { n: "I.",   t: "Arrive ten minutes early", b: "Tea is poured. The room is warm. You'll fill in a short intake — nothing more than what's necessary." },
              { n: "II.",  t: "A gentle conversation",    b: "Three minutes of listening. We learn what your body needs that day, what to avoid, where to linger." },
              { n: "III.", t: "The session",              b: "Phones away. Lights low. You may sleep. You may cry. Both are welcome." },
              { n: "IV.",  t: "Slow re-entry",            b: "Water, dates, a soft chair. We don't rush you back into the world." },
            ].map((s) => (
              <div key={s.n} className="border-t border-[#E2DCD0] pt-6">
                <div className="flex items-baseline gap-6 mb-2">
                  <span className="font-serif italic text-2xl text-[#9F5C45]">{s.n}</span>
                  <h3 className="font-serif text-2xl md:text-3xl text-[#1C1917]">{s.t}</h3>
                </div>
                <p className="text-[#57534E] text-base leading-relaxed md:pl-12">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 bg-[#2C4C3B] text-[#FDFBF7]" data-testid="spa-cta">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="overline text-[#EAE5D9] opacity-70 mb-6">Ready to begin?</p>
          <h2 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-10">
            The body is <em className="italic text-[#EAE5D9]">listening.</em>
            <br />
            So are we.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#book"
              data-testid="spa-final-book"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FDFBF7] text-[#2C4C3B] hover:bg-[#EAE5D9] transition-colors text-sm tracking-wide"
            >
              Book your session <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <a
              href={`tel:${(site?.phone || "").replace(/\s+/g, "")}`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#FDFBF7]/30 hover:bg-[#FDFBF7]/10 transition-colors text-sm tracking-wide"
            >
              Call {site?.phone || ""}
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <CrisisBanner />
      <CallButton />
    </main>
  );
}
